import type React from 'react';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { customScrollbar } from '@/styles/style';
import zIndex from '@/styles/zIndex';

import { CenterFlex, Flex } from '../base';
import Checkbox from '../Checkbox/Checkbox';
import Spinner from '../Common/Spinner';
import { MainText } from '../Typography/NamedTypo';
import DataColumnItem from './DataColumnItem';
import DataRowItem from './DataRowItem';

const DEFAULT_DATA_TABLE_ID = 'default-data-table-id';
export const CHECKBOX_COLUMN_WIDTH = 28;
const COLUMN_MIN_SIZE = 50;

const DataTable = (props: DataTableT) => {
  const [domColumnConfigs, setDomColumnConfigs] = useState<ColumnConfigsResT[]>(
    [],
  );

  useEffect(() => {
    setDomColumnConfigs(
      props.columns
        .filter(
          (col) => !col.isDefaultColumn && !col.canNotBeShown && col.visible,
        )
        .map((col) => ({
          name: col.columnKey,
          order: col.columnOrderIndex!,
          width: col.width,
          visible: col.visible || false,
        })),
    );
  }, [props.columns]);

  const id = useMemo(() => {
    return props.id || DEFAULT_DATA_TABLE_ID;
  }, [props.id]);

  const onResizeState = useRef<{
    target: string;
    width: number;
    element: HTMLDivElement;
  } | null>(null);

  const [onChangeOrderState, setOnChangeOrderState] = useState<{
    target: string;
    initialOrder?: number;
    order?: number;
  } | null>(null);

  const setOnResizeState = (
    state: {
      target: string;
      width: number;
      element: HTMLDivElement;
    } | null,
  ) => {
    onResizeState.current = state;
  };

  const visibleColumns = useMemo(() => {
    return props.columns.filter((col) => {
      if (!col) return false;
      return col?.visible;
    });
  }, [props.columns]);

  const changeDomColumnWidth = useCallback(
    (key: string, width: number) => {
      setDomColumnConfigs?.((prev) => {
        const newConfigs = [...prev];
        const targetIndex = newConfigs.findIndex(
          (config) => config.name === key,
        );
        if (targetIndex === -1) return newConfigs;
        newConfigs[targetIndex].width = width;
        return newConfigs;
      });
    },
    [setDomColumnConfigs],
  );

  const changeDomColumnOrder = useCallback(
    (key: string, order: number) => {
      setDomColumnConfigs?.((prev) => {
        const newConfigs = [...prev].sort((a, b) => {
          return a.order - b.order;
        });
        const targetIndex = newConfigs.findIndex(
          (config) => config.name === key,
        );
        const newConfig = { ...newConfigs[targetIndex] };
        newConfigs.splice(targetIndex, 1);
        newConfigs.splice(order, 0, newConfig);
        const result = newConfigs.map((config, index) => ({
          ...config,
          order: index,
        }));
        return result;
      });
    },
    [setDomColumnConfigs],
  );

  const onColumnMouseOver = (
    e: React.MouseEvent,
    {
      isDefaultColumn,
      columnKey,
      domIndex,
    }: {
      isDefaultColumn?: boolean;
      columnKey: string;
      domIndex?: number;
    },
  ) => {
    if (isDefaultColumn) return;
    if (onResizeState?.current) {
      e.stopPropagation();
      document.body.style.cursor = 'ew-resize';
      if (e.clientX === 0) return;
      const el = onResizeState.current.element;
      const rect = el.getBoundingClientRect();
      const width = e.clientX - rect.left;
      if (width <= COLUMN_MIN_SIZE) {
        setOnResizeState?.({
          ...onResizeState.current,
          width: COLUMN_MIN_SIZE,
        });
      } else {
        setOnResizeState?.({
          ...onResizeState.current,
          width,
        });
      }

      changeDomColumnWidth(
        onResizeState.current.target,
        onResizeState.current.width,
      );
      return;
    }
    if (!onChangeOrderState) return;

    if (columnKey === onChangeOrderState.target) return;
    if (!e.currentTarget) return;

    const currTarget = e.currentTarget as HTMLDivElement;
    document.body.style.cursor = 'grabbing';

    const rect = currTarget.getBoundingClientRect();
    const center = rect.left + rect.width / 2;

    let targetIndex = domIndex as number;

    if ((onChangeOrderState.initialOrder || 0) < (domIndex || 0)) {
      // 뒤로 옮기는 경우
      if (center > e.clientX) {
        targetIndex += -1;
      }
    } else {
      // 앞으로 옮기는 경우
      if (center < e.clientX) {
        targetIndex += 1;
      }
    }

    if (targetIndex === onChangeOrderState.initialOrder) return;

    changeDomColumnOrder(onChangeOrderState.target, targetIndex);

    setOnChangeOrderState?.({
      ...onChangeOrderState,
      initialOrder: targetIndex,
      order: targetIndex,
    });
  };

  const windowMouseUp = useCallback(() => {
    document.body.style.cursor = 'default';
    if (onResizeState?.current) {
      props.columnResizeHandler?.(
        onResizeState.current.target,
        Math.round(onResizeState.current.width), // convert float to integer
      );
      setOnResizeState?.(null);
      return;
    }
    if (typeof onChangeOrderState?.order === 'undefined') return;

    changeDomColumnOrder(onChangeOrderState.target, onChangeOrderState.order);

    setTimeout(() => {
      if (typeof onChangeOrderState?.order === 'undefined') return;
      props.columnChangeOrderHandler?.(
        onChangeOrderState.target,
        onChangeOrderState.order,
      );
    }, 1);

    setOnChangeOrderState?.(null);
  }, [
    changeDomColumnOrder,
    onChangeOrderState?.order,
    onChangeOrderState?.target,
    props,
  ]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('mouseup', windowMouseUp);
    }
    return () => {
      window.removeEventListener('mouseup', windowMouseUp);
    };
  }, [windowMouseUp]);

  return (
    <Style d="column" style={props.style} tableHeight={props.tableHeight}>
      <DataTableWrapper
        id={id}
        style={{
          height: '100%',
        }}
      >
        <DataTableElement
          style={{
            width: visibleColumns.reduce(
              (acc, value) => {
                acc += value.width || 0;
                return acc;
              },
              props.checkable ? CHECKBOX_COLUMN_WIDTH : 0,
            ),
          }}
        >
          <HeaderWrapper>
            <DataTableHeader>
              {props.checkable && (
                <CheckboxTh>
                  <Checkbox
                    checked={
                      props.checkedList?.length !== 0 &&
                      props.datas.length === props.checkedList?.length
                    }
                    halfCheck={
                      props.checkedList?.length !== 0 &&
                      props.datas.length !== props.checkedList?.length
                    }
                    onChange={() => {
                      const filtered = props.datas
                        .filter((data) => data.visible !== false)
                        .map((data) => data.rowKey);

                      let result: string[] = [];

                      if (filtered.length !== props.checkedList?.length) {
                        result = [...filtered];
                      }

                      props.onCheckListChange?.(result);
                    }}
                  />
                </CheckboxTh>
              )}
              {visibleColumns
                .filter((col) => col.isDefaultColumn)
                .map((col, index, arr) => {
                  const config = domColumnConfigs.find(
                    (config) => config.name === col.columnKey,
                  );
                  const accumulatedWidth = arr.reduce(
                    (acc, curr, idx) => {
                      if (idx >= index) return acc;
                      const width = arr[idx].width || 0;
                      return acc + width;
                    },
                    props.checkable ? 40 : 0,
                  );

                  return (
                    <DataColumnItem
                      key={col.columnKey}
                      fixed={col.isDefaultColumn}
                      offset={accumulatedWidth}
                      column={{
                        ...col,
                        width: col.width || config?.width,
                        onOrderClick: props.onSortChange,
                        orderStatus:
                          props.sortStatus?.sortBy === col.columnKey
                            ? props.sortStatus.orderStatus
                            : 'unset',
                      }}
                    />
                  );
                })}
              {visibleColumns
                .filter((col) => !col.isDefaultColumn)
                .sort((a, b) => {
                  const aConfig = domColumnConfigs.find(
                    (config) => config.name === a.columnKey,
                  );
                  const bConfig = domColumnConfigs.find(
                    (config) => config.name === b.columnKey,
                  );
                  return (aConfig?.order || 0) - (bConfig?.order || 0);
                })
                .map((col, index, arr) => {
                  const config = domColumnConfigs.find(
                    (config) => config.name === col.columnKey,
                  );

                  return (
                    <DataColumnItem
                      key={col.columnKey}
                      domIndex={index}
                      column={{
                        ...col,
                        width: config?.width,
                        onOrderClick: props.onSortChange,
                        orderStatus:
                          props.sortStatus?.sortBy === col.columnKey
                            ? props.sortStatus.orderStatus
                            : 'unset',
                      }}
                      onResizeState={onResizeState}
                      onChangeOrderState={onChangeOrderState}
                      setOnResizeState={setOnResizeState}
                      setOnChangeOrderState={setOnChangeOrderState}
                      onColumnMouseOver={onColumnMouseOver}
                      setDomColumnConfigs={setDomColumnConfigs}
                      columnResizeHandler={props.columnResizeHandler}
                      columnChangeOrderHandler={props.columnChangeOrderHandler}
                    />
                  );
                })}
              {props.customizable && (
                <div
                  onMouseMove={(e) =>
                    onColumnMouseOver(e, {
                      columnKey: 'dummy',
                    })
                  }
                ></div>
              )}
            </DataTableHeader>
          </HeaderWrapper>
          <DataTableBody isLoading={!!props.loading}>
            {props.datas.length > 0 ? (
              props.datas.map((row) => (
                <DataRowItem
                  key={row.rowKey}
                  name={props.name}
                  parentId={id}
                  selected={row.rowKey === props.selectedRowKey}
                  row={row}
                  domColumnConfigs={domColumnConfigs}
                  columns={visibleColumns}
                  checkable={props.checkable}
                  openButtonOffsetX={props.openButtonOffsetX}
                  onCheck={(rowKey, status) => {
                    let result: string[] = [...(props.checkedList || [])];
                    if (status) {
                      result.push(rowKey);
                    } else {
                      result = result.filter((key) => key !== rowKey);
                    }

                    props.onCheckListChange?.(result);
                  }}
                  checkedList={props.checkedList || []}
                  visible={row.visible}
                />
              ))
            ) : props.loading ? (
              <CenterFlex style={{ height: 300 }}>
                <Spinner size={16} />
              </CenterFlex>
            ) : (
              <CenterFlex style={{ height: 300 }}>
                {typeof props.emptyDataPlaceholder === 'string' ||
                typeof props.emptyDataPlaceholder === 'undefined' ? (
                  <MainText dim>
                    {props.emptyDataPlaceholder || 'No data'}
                  </MainText>
                ) : (
                  props.emptyDataPlaceholder
                )}
              </CenterFlex>
            )}
          </DataTableBody>
        </DataTableElement>
        {props.isFetchingNextPage ? (
          <CenterFlex height="40px">
            <Spinner />
          </CenterFlex>
        ) : (
          props.infiniteRef &&
          props.hasNextPage && <InfiniteRefTarget ref={props.infiniteRef} />
        )}
      </DataTableWrapper>
    </Style>
  );
};

const Style = styled(Flex)<{ tableHeight?: string }>`
  height: ${({ tableHeight }) => tableHeight || '100%'};
`;

export const DataTableElement = styled(Flex).attrs({ d: 'column' })`
  min-width: 100%;
`;

const DataTableWrapper = styled.div`
  scroll-behavior: smooth;
  width: 100%;
  overflow: auto;
  ${customScrollbar};
`;

export const HeaderWrapper = styled(Flex)`
  position: sticky;
  top: 0;
  border-bottom: 1px solid ${(props) => props.theme.colors.ND04};
  z-index: ${zIndex.tableHeader};
`;

export const DataTableHeader = styled(Flex)`
  min-width: fit-content;
  width: 100%;
  height: 36px;
  background-color: ${(props) => props.theme.colors.gray100};
`;

type TableBodyProps = {
  isLoading?: boolean;
};

export const DataTableBody = styled(Flex).attrs({
  d: 'column',
})<TableBodyProps>`
  min-width: fit-content;
  width: 100%;
  opacity: ${({ isLoading }) => (isLoading ? 0.5 : 1)};
  transition: all 0.2s;
`;

const CheckboxTh = styled(CenterFlex)`
  position: sticky;
  width: 40px;
  left: 0px;
  z-index: 1;
  background-color: ${(props) => props.theme.colors.gray100};
`;

const InfiniteRefTarget = styled.div`
  height: 40px;
`;

export default memo(DataTable);
